import { Link, useLocation } from "@tanstack/react-router";
import {
  Menu, X, LayoutDashboard, Home, Briefcase, HelpCircle, Package, Plane,
  Upload, CreditCard, FileText, BookOpen, MessageCircleQuestion, Phone, UserPlus

} from "lucide-react";
import { lazy, Suspense, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Logo } from "./Logo";
// Defer non-critical navbar widgets — they're not part of the LCP element
// and shouldn't block the route's main JS execution on mobile.
const ThemeToggle = lazy(() =>
  import("./ThemeToggle").then((m) => ({ default: m.ThemeToggle })),
);
const LanguageSwitcher = lazy(() =>
  import("./LanguageSwitcher").then((m) => ({ default: m.LanguageSwitcher })),
);
const NotificationBell = lazy(() =>
  import("./NotificationBell").then((m) => ({ default: m.NotificationBell })),
);
import { useAuth } from "@/hooks/use-auth";

function NavControlFallback({ wide = false }: { wide?: boolean }) {
  return <span className={wide ? "nav-control-fallback nav-control-fallback--wide" : "nav-control-fallback"} aria-hidden="true" />;
}

const linkDefs = [
  { to: "/", key: "home", icon: Home },
  { to: "/services", key: "services", icon: Briefcase },
  { to: "/how-it-works", key: "howItWorks", icon: HelpCircle },
  { to: "/packages", key: "packages", icon: Package },
  { to: "/visa-tax", key: "visaTax", icon: Plane },
  { to: "/upload", key: "upload", icon: Upload },
  { to: "/payment", key: "payment", icon: CreditCard },
  { to: "/blog", key: "blog", icon: FileText },
  { to: "/knowledge-hub", key: "knowledgeHub", icon: BookOpen },
  { to: "/faq", key: "faq", icon: MessageCircleQuestion },
  { to: "/contact", key: "contact", icon: Phone },
] as const;

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { user } = useAuth();
  const { t } = useTranslation();
  const links = linkDefs.map((l) => ({ ...l, label: t(`nav.${l.key}`) }));

  useEffect(() => {
    let lastY = window.scrollY;
    let ticking = false;
    const update = () => {
      const y = window.scrollY;
      setScrolled((prev) => (prev !== y > 8 ? y > 8 : prev));
      if (y > 120 && y > lastY + 4) setHidden(true);
      else if (y < lastY - 4 || y < 80) setHidden(false);
      lastY = y;
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header
      className={`site-header sticky top-0 z-50 transition-transform duration-150 ease-out ${scrolled ? "py-1.5" : "py-3"} ${hidden && !open ? "-translate-y-full" : "translate-y-0"}`}
    >
      <div className="container-page">
        <nav
          className={`site-nav glass flex flex-col transition-all duration-150 ease-out ${
            scrolled
              ? "rounded-3xl px-3 md:px-5 py-2 shadow-xl shadow-emerald-500/10 backdrop-blur-2xl bg-white/70 dark:bg-slate-900/70 border border-emerald-500/20 w-full gap-2"
              : "rounded-2xl px-4 md:px-6 py-3 gap-3"
          }`}
        >
          {/* Row 1: Logo only (with right-side actions) */}
          <div className="site-nav-row flex items-center justify-between gap-3">
            <Logo size="lg" />
            <div className="nav-actions flex items-center gap-1.5 rounded-full backdrop-blur-md bg-white/5 dark:bg-white/5 border border-white/10 dark:border-white/10 px-1.5 py-1 shadow-sm">
              <Suspense fallback={<><NavControlFallback wide /><NavControlFallback /></>}>
                {!pathname.startsWith("/admin") && (
                  <div className="rounded-full hover:bg-white/10 transition-colors p-0.5">
                    <LanguageSwitcher />
                  </div>
                )}
                {user && (
                  <div className="rounded-full hover:bg-white/10 transition-colors p-0.5">
                    <NotificationBell />
                  </div>
                )}
                <div className="rounded-full hover:bg-white/10 transition-colors p-0.5">
                  <ThemeToggle />
                </div>
              </Suspense>
              <div className="desktop-auth hidden md:flex items-center gap-2 ml-1">
                {user ? (
                  <Button asChild size="sm" className="relative gradient-primary text-primary-foreground shadow-glow hover:opacity-95 ring-1 ring-emerald-300/40 hover:ring-emerald-200/70 transition-all">
                    <Link to="/dashboard" title="Dashboard"><LayoutDashboard className="h-4 w-4 mr-1.5" /> <span className="bg-gradient-to-r from-white to-emerald-100 bg-clip-text text-transparent font-semibold">{t("nav.dashboard")}</span></Link>
                  </Button>
                ) : (
                  <>
                    <Button asChild variant="ghost" size="sm"><Link to="/login" title="Login">{t("nav.login")}</Link></Button>
                    <Link
                      to="/signup"
                      title="Sign up"
                      aria-label={t("nav.signup")}
                      className="flex flex-col items-center justify-center cursor-pointer hover:scale-105 transition-transform ml-0.5"
                    >
                      <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-tr from-blue-600 to-sky-400 shadow-glow ring-1 ring-sky-300/40">
                        <UserPlus className="w-5 h-5 text-white" />
                      </span>
                      <span className="text-[10px] font-medium mt-0.5 tracking-wide leading-none">{t("nav.signup")}</span>
                    </Link>

                  </>
                )}
              </div>
              {/* Mobile-only compact auth buttons (<768px). Hidden on md+ where desktop-auth takes over. */}
              <div className="mobile-auth flex md:hidden items-center gap-1 ml-0.5">
                {user ? (
                  <Link
                    to="/dashboard"
                    title="Dashboard"
                    aria-label="Dashboard"
                    className="inline-flex h-8 items-center justify-center rounded-full gradient-primary text-primary-foreground px-2.5 text-xs font-semibold shadow-glow ring-1 ring-emerald-300/40"
                  >
                    <LayoutDashboard className="h-3.5 w-3.5" />
                  </Link>
                ) : (
                  <>
                    <Link
                      to="/login"
                      title="Login"
                      className="inline-flex h-8 items-center justify-center rounded-full px-2.5 text-xs font-semibold text-foreground/90 hover:bg-white/10 transition-colors"
                    >
                      {t("nav.login")}
                    </Link>
                    <Link
                      to="/signup"
                      title="Sign up"
                      aria-label={t("nav.signup")}
                      className="flex flex-col items-center justify-center cursor-pointer hover:scale-105 transition-transform"
                    >
                      <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-tr from-blue-600 to-sky-400 shadow-glow ring-1 ring-sky-300/40">
                        <UserPlus className="w-5 h-5 text-white" />
                      </span>
                      <span className="text-[10px] font-medium mt-0.5 tracking-wide leading-none">{t("nav.signup")}</span>
                    </Link>

                  </>
                )}
              </div>
              <button
                type="button"
                className="mobile-menu-button lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-full hover:bg-white/10 active:bg-white/20 transition-colors touch-manipulation select-none"
                style={{ WebkitTapHighlightColor: "transparent" }}
                onClick={() => setOpen((v) => !v)}
                aria-label="Toggle menu"
                aria-expanded={open}
              >
                {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>

            </div>
          </div>

          {/* Row 2: Navigation links box */}
          <ul
            className="nav-links hidden lg:flex flex-nowrap items-center justify-between gap-1 xl:gap-2 rounded-2xl bg-emerald-500/10 dark:bg-emerald-400/10 border border-emerald-500/20 px-2 py-2 shadow-sm w-full"
            style={{ fontFamily: "'Hind Siliguri', 'Noto Sans Bengali', system-ui, -apple-system, 'Inter', sans-serif" }}
          >
            {links.map((l) => {
              const hash = (l as any).hash as string | undefined;
              const active = !hash && pathname === l.to;
              const isVisa = l.key === "visaTax";
              return (
                <li key={l.label} className="shrink-0">
                  <Link
                    to={l.to}
                    hash={hash}
                    title={l.label}
                    aria-label={l.label}
                    preload="intent"
                    className={`inline-flex items-center justify-center px-3.5 xl:px-5 py-2.5 text-[15px] xl:text-[16px] font-bold leading-none rounded-full transition-all duration-150 whitespace-nowrap tracking-tight antialiased subpixel-antialiased cursor-pointer hover:scale-[1.02] ${
                      isVisa
                        ? "text-neutral-950 dark:text-white bg-sky-500/15 ring-1 ring-sky-400/30 hover:bg-gradient-to-r hover:from-sky-500 hover:to-cyan-500 hover:text-white hover:shadow-lg hover:shadow-sky-500/40"
                        : active
                          ? "text-neutral-950 dark:text-white bg-gradient-to-r from-emerald-500/30 to-teal-500/30 ring-1 ring-emerald-400/50 shadow-md shadow-emerald-500/20"
                          : "text-neutral-950 dark:text-white ring-1 ring-transparent hover:bg-gradient-to-r hover:from-emerald-500 hover:to-teal-500 hover:text-white hover:shadow-lg hover:shadow-emerald-500/40"
                    }`}
                  >
                    {l.label}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {open && (
          <div
            className="lg:hidden mt-2 glass rounded-2xl p-3 shadow-soft animate-in fade-in slide-in-from-top-2 duration-150"
          >
            <ul className="flex flex-col gap-2">
              {links.map((l, i) => {
                const hash = (l as any).hash as string | undefined;
                const isVisa = l.key === "visaTax";
                const isGreen = i % 2 === 0;
                const itemClass = isGreen
                  ? "bg-gradient-to-r from-emerald-500 to-green-600 text-white border border-emerald-600/30"
                  : "bg-gradient-to-r from-orange-400 to-orange-500 dark:from-white dark:to-slate-100 text-black border border-emerald-600/30";
                return (
                  <li key={l.label}>
                    <Link
                      to={l.to}
                      hash={hash}
                      title={l.label}
                      aria-label={l.label}
                      preload={false}
                      onClick={() => {
                        setOpen(false);
                      }}
                      style={{ WebkitTapHighlightColor: "transparent" }}
                      className={`flex items-center justify-center text-center px-5 py-4 text-xl font-bold rounded-xl shadow-sm active:scale-[0.98] transition-transform touch-manipulation select-none ${itemClass}`}
                    >
                      {l.label}
                    </Link>
                  </li>
                );
              })}
              <li className="mt-1 rounded-xl border border-emerald-500/30 bg-emerald-50/70 dark:bg-emerald-500/10 p-3">
                <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-800 dark:text-emerald-200 mb-2">Individual Services</p>
                <div className="grid grid-cols-1 gap-1.5 text-sm font-semibold">
                  <Link to="/services/$slug" params={{ slug: "income-tax-return" }} title="Income Tax Return Filing" onClick={() => setOpen(false)} className="text-emerald-900 dark:text-emerald-100 hover:underline">› Income Tax Return</Link>
                  <Link to="/services/$slug" params={{ slug: "vat-registration" }} title="Online VAT Registration (BIN)" onClick={() => setOpen(false)} className="text-emerald-900 dark:text-emerald-100 hover:underline">› VAT Registration</Link>
                  <Link to="/services/$slug" params={{ slug: "tin-registration" }} title="e-TIN Registration & Recovery" onClick={() => setOpen(false)} className="text-emerald-900 dark:text-emerald-100 hover:underline">› TIN Registration</Link>
                  <Link to="/services/$slug" params={{ slug: "rjsc-company-registration" }} title="RJSC Company Registration" onClick={() => setOpen(false)} className="text-emerald-900 dark:text-emerald-100 hover:underline">› Company Tax / RJSC</Link>
                  <Link to="/services/$slug" params={{ slug: "trade-license" }} title="Trade License Support" onClick={() => setOpen(false)} className="text-emerald-900 dark:text-emerald-100 hover:underline">› Trade License</Link>
                </div>
              </li>
              <li className="grid grid-cols-2 gap-2 mt-2 pt-2 border-t">
                {user ? (
                  <Button asChild size="sm" className="col-span-2 gradient-primary text-primary-foreground"><Link to="/dashboard" title="Dashboard" onClick={() => setOpen(false)}>{t("nav.myDashboard")}</Link></Button>
                ) : (
                  <>
                    <Button asChild variant="outline" size="sm"><Link to="/login" title="Login" onClick={() => setOpen(false)}>{t("nav.login")}</Link></Button>
                    <Button asChild size="sm" className="bg-sky-500 hover:bg-sky-600 text-white"><Link to="/signup" title="Sign up" onClick={() => setOpen(false)}>{t("nav.signup")}</Link></Button>
                  </>
                )}
              </li>
            </ul>
          </div>
        )}

      </div>
    </header>
  );
}
